import type { IAddServiceCmd } from "@/data/commands/room/AddServiceCmd";
import type { ICreateRoomCmd } from "@/data/commands/room/CreateRoomCmd";
import type { IUpdateRoomKioskHeaderCmd } from "@/data/commands/room/UpdateRoomKioskHeaderCmd";
import type { IUpdateServiceCmd } from "@/data/commands/room/UpdateServiceCmd";
import type { IUpdateServiceOrderCmd } from "@/data/commands/room/UpdateServiceOrderCmd";
import type {
    IRoom,
    IRoomService,
    IRoomServiceOrderUseCase,
    IRoomServicePayUseCase,
    IRoomWithService,
} from "@/data/model/Room";
import type { IService } from "@/data/model/Service";
import type { IRoomsQry } from "@/data/queries/room/RoomsQry";
import type { IServiceQry } from "@/data/queries/room/ServiceQry";

import {
    BaseServiceDao,
    type IEditableDataSource,
    type IImageDataSource,
    type IPaginatedResult,
    parseCollection,
    parseMultiLanguageText,
    parsePaginatedResult,
} from "./BaseServiceDao";
import { PlanningDao } from "./PlanningDao";

export class RoomDao extends BaseServiceDao implements IEditableDataSource<IRoomWithService> {
    public static className = "RoomDao";

    constructor() {
        super("room");
        this.isLocationDependent = true;
    }

    public async getSingle(id: string): Promise<IRoomWithService> {
        return this.get(id, undefined, RoomDao.parseRoomWithService);
    }

    public async getData(qry: IRoomsQry): Promise<IPaginatedResult<IRoomWithService>> {
        return this.get("", qry, (f) => parsePaginatedResult(f, RoomDao.parseRoomWithService));
    }

    public async create(cmd: ICreateRoomCmd): Promise<IRoomWithService> {
        return this.post("", cmd, RoomDao.parseRoomWithService);
    }

    public async update(id: string, cmd: ICreateRoomCmd): Promise<IRoomWithService> {
        return this.put(id, cmd, RoomDao.parseRoomWithService);
    }

    public async updateKioskHeader(id: string, cmd: IUpdateRoomKioskHeaderCmd): Promise<IRoomWithService> {
        return this.put(id + "/kiosk-header", cmd, RoomDao.parseRoomWithService);
    }

    public async remove(id: string): Promise<void> {
        return this.delete(id, {});
    }

    public async getServices(roomId: string, qry: IServiceQry): Promise<Array<IService>> {
        return this.get(roomId + "/service/", qry, (f) => parseCollection(f, RoomDao.parseService));
    }

    public async addService(roomId: string, cmd: IAddServiceCmd): Promise<IService> {
        return this.post(roomId + "/service/", cmd, RoomDao.parseService);
    }

    public async updateService(roomId: string, serviceId: string, cmd: IUpdateServiceCmd): Promise<IService> {
        return this.put(roomId + "/service/" + serviceId, cmd, RoomDao.parseService);
    }

    public async updateServiceOrder(roomId: string, cmd: IUpdateServiceOrderCmd): Promise<void> {
        return this.put(roomId + "/service/order", cmd);
    }

    public async removeService(roomId: string, serviceId: string): Promise<void> {
        return this.delete(roomId + "/service/" + serviceId, {});
    }

    public async setServiceImage(roomId: string, serviceId: string, file: File): Promise<string> {
        return this.post(roomId + "/service/" + serviceId + "/image", { Image: file }, (c) => c.path, true);
    }

    public async removeServiceImage(roomId: string, serviceId: string): Promise<void> {
        return this.delete(roomId + "/service/" + serviceId + "/image", {});
    }

    public serviceImageDataSource(roomId: string): IImageDataSource<IService> {
        return {
            create: (m, file) => this.setServiceImage(roomId, m.id, file),
            remove: (m) => this.removeServiceImage(roomId, m.id),
            valueAccessor: (m, setter) => {
                if (setter !== undefined) m.imagePath = setter == null ? undefined : setter;

                return m.imagePath;
            },
        };
    }

    public static parseRoom(literal: any): IRoom {
        return {
            id: literal.id,
            locationId: literal.locationId,
            isActive: literal.isActive,
            name: literal.name,
            order: literal.order == null ? undefined : parseInt(literal.order),
        };
    }

    public static parseRoomWithService(literal: any): IRoomWithService {
        return {
            ...RoomDao.parseRoom(literal),
            shape: parseInt(literal.shape),
            deallocationDelay: literal.deallocationDelay == null ? undefined : literal.deallocationDelay,
            isAutomaticCheckoutEnabled: literal.isAutomaticCheckoutEnabled,
            endUserServices: parseCollection(literal.endUserServices, (c) => RoomDao.parseRoomService(c)),
            operatorServices: parseCollection(literal.operatorServices, (c) => RoomDao.parseRoomService(c)),
            isPrintReceiptAvailable: literal.isPrintReceiptAvailable,
            kioskHeader: literal.kioskHeader == null ? undefined : parseMultiLanguageText(literal.kioskHeader),
        };
    }

    public static parseRoomService(literal: any): IRoomService {
        return {
            id: literal.id,
            name: parseMultiLanguageText(literal.name),
            order: parseInt(literal.order),
            serviceType: parseInt(literal.serviceType),
            orderUseCase:
                literal.orderUseCase == null ? undefined : RoomDao.parseRoomOrderUseCase(literal.orderUseCase),
            payUseCase: literal.payUseCase == null ? undefined : RoomDao.parseRoomPayUseCase(literal.payUseCase),
            availableFor: parseInt(literal.availableFor),
        };
    }

    public static parseRoomOrderUseCase(literal: any): IRoomServiceOrderUseCase {
        return {
            id: literal.id,
            name: parseMultiLanguageText(literal.name),
        };
    }

    public static parseRoomPayUseCase(literal: any): IRoomServicePayUseCase {
        return {
            id: literal.id,
            name: parseMultiLanguageText(literal.name),
        };
    }

    public static parseService(literal: any): IService {
        return {
            id: literal.id,
            iconClass: literal.iconClass == null ? undefined : literal.iconClass,
            imagePath: literal.imagePath == null ? undefined : literal.imagePath,
            order: parseInt(literal.order),
            config: literal.config,
            serviceType: parseInt(literal.serviceType),
            useCase: literal.useCase == null ? undefined : PlanningDao.parseOrderApplication(literal.useCase),
            payUseCase: literal.payUseCase == null ? undefined : PlanningDao.parsePayApplication(literal.payUseCase),
            name: parseMultiLanguageText(literal.name),
            availableFor: parseInt(literal.availableFor),
        };
    }
}
